import { Tissue, TissueUses } from "../models";

export function getIsTissueReadyForInvoice(tissue: Tissue | undefined) {
    if (!tissue?.isActive || !tissue.placement?.tissueUse) return false;

    const surgery = tissue.surgery ?? tissue.backupSurgery;

    // assume here that there is not yet an invoice line for this tissue

    const missingSurgeryInfo =
        !surgery?.locationAccountId ||
        !surgery.billingAccountId ||
        !surgery.surgeonId ||
        !surgery.scheduledOnUtc ||
        (!surgery.poNumber && surgery.billingAccount?.poRequiredToInvoice);

    const isTransplant = tissue.placement?.tissueUse === TissueUses.Transplant;

    if (isTransplant && missingSurgeryInfo) return false;

    const hasFee =
        (tissue.placement?.feeTissueFee ?? 0) > 0 ||
        (tissue.placement?.feeProcessingFee ?? 0) > 0 ||
        (tissue.placement?.feeShippingFee ?? 0) > 0;
    if (!hasFee) return false;

    const isImported = !!tissue.sourceAccountId;

    if (!tissue.isShippedDirect && !tissue.orders?.length) return false;

    if (isImported && tissue.isShippedDirect) {
        return true;
    }

    const order = tissue?.orders?.[0]; // API should return the latest order first
    if (
        !order?.shipment?.shippedOn ||
        !order?.shipment?.fromAccountId ||
        !order?.shipment?.toAccountId ||
        !order?.shipment?.shippingMethodId ||
        order?.shipment?.toAccount?.isInternal
    )
        return false;

    const isExported = order.exportedTissue;
    if (
        !isExported &&
        (!surgery?.recipientFirstName || !surgery?.recipientLastName) &&
        !surgery?.billingAccount?.isInternational
    )
        return false;

    return true;
}

export function getReasonsTissueNotReadyForInvoice(tissue: Tissue | undefined) {
    const reasons: string[] = [];

    if (!tissue?.isActive) {
        reasons.push("Tissue has been deleted.");
        return reasons;
    }

    const surgery = tissue.surgery ?? tissue.backupSurgery;
    const isTransplant = tissue.placement?.tissueUse === TissueUses.Transplant;
    const hasFee =
        (tissue.placement?.feeTissueFee ?? 0) > 0 ||
        (tissue.placement?.feeProcessingFee ?? 0) > 0 ||
        (tissue.placement?.feeShippingFee ?? 0) > 0;
    const poRequired = surgery?.billingAccount?.poRequiredToInvoice;

    if (!hasFee) {
        reasons.push("Tissue has no fee.");
    }

    if (!tissue.placement?.tissueUse) {
        reasons.push("Tissue use is unspecified.");
    } else if (isTransplant) {
        if (!surgery?.locationAccountId) {
            reasons.push("Missing surgery location.");
        }
        if (!surgery?.billingAccountId) {
            reasons.push("Missing surgery billing account.");
        } else if (poRequired && !surgery?.poNumber) {
            reasons.push("Missing P.O. number.");
        }
        if (!surgery?.surgeonId) {
            reasons.push("Missing surgeon.");
        }
        if (!surgery?.scheduledOnUtc) {
            reasons.push("Missing surgery date.");
        }
    }

    const isImported = !!tissue.sourceAccountId;
    if (isImported && tissue.isShippedDirect) {
        return reasons;
    }

    let isExported = false;

    if (!tissue.isShippedDirect && !tissue.orders?.length) {
        reasons.push("Tissue has no orders.");
    } else {
        const order = tissue?.orders?.[0]; // API should return the latest order first
        isExported = order?.exportedTissue ?? false;

        if (!order?.shipment?.shippedOn) {
            reasons.push("Order has not been shipped.");
        }
        if (!order?.shipment?.fromAccountId) {
            reasons.push("Order needs shipping origin.");
        }
        if (!order?.shipment?.toAccountId) {
            reasons.push("Order needs shipping destination.");
        }
        if (!order?.shipment?.shippingMethodId) {
            reasons.push("Order needs shipping method.");
        }
        if (order?.shipment?.toAccount?.isInternal) {
            reasons.push("Order was last shipped to an internal account.");
        }
    }

    if (
        !isExported &&
        !surgery?.recipientFirstName &&
        !surgery?.recipientLastName &&
        !surgery?.billingAccount?.isInternational
    ) {
        reasons.push("Missing recipient name (or need to mark as exported to eye bank).");
    }

    return reasons;
}
